/**
 * Preferences › Screen and pointer (desktop; settings pocket spec §6):
 * what agents may do with the pointer. macOS
 * shows the pointer policy and the system grants it needs, re-read on open
 * and whenever the window regains focus; Windows shows Denker's own consent
 * switch. Both show the experimental Denker browser. Rust owns every value.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  PocketRow,
  PocketSection,
  SelectField,
  ToggleSwitch,
} from "@/components/ui";
import {
  getInputConsent,
  inputConsentApplies,
  setInputConsent,
} from "@/desktop/input-consent";
import { openPrivacyPaneForCurrentMacOS } from "@/desktop/onboarding/permissions-deep-links";
import {
  getPointerControlStatus,
  pointerControlApplies,
  setPointerControlPolicy,
  type PointerControlPolicy,
  type PointerControlStatus,
} from "@/desktop/pointer-control-policy";
import { translate, useT } from "@/i18n";
import { settingsPreferencesMessages } from "@/i18n/messages/settings-preferences";
import { SettingsFieldError } from "./settings-text-field";
import { ScreenRecordingFolderRow } from "./screen-recording-folder-row";

const POLICY_LABEL_KEY = {
  ask_every_task: "policyAsk",
  always_allow: "policyAlways",
  never_allow: "policyNever",
} as const satisfies Record<
  PointerControlPolicy,
  keyof typeof settingsPreferencesMessages.en
>;

function usePointerControl(applies: boolean) {
  const [status, setStatus] = useState<PointerControlStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const writing = useRef(false);
  const revision = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++revision.current;
    try {
      const result = await getPointerControlStatus();
      if (request === revision.current) {
        setStatus(result);
        setError(null);
      }
    } catch {
      if (request === revision.current) {
        setError(translate(settingsPreferencesMessages, "pointerReadFailed"));
      }
    }
  }, []);

  useEffect(() => {
    if (!applies) return;
    void refresh();
    const onFocus = () => {
      if (!writing.current) void refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      revision.current += 1;
      window.removeEventListener("focus", onFocus);
    };
  }, [applies, refresh]);

  const save = useCallback(
    async (policy: PointerControlPolicy) => {
      if (writing.current) return;
      writing.current = true;
      revision.current += 1;
      setSaving(true);
      setError(null);
      try {
        await setPointerControlPolicy(policy);
        await refresh();
      } catch {
        setError(translate(settingsPreferencesMessages, "pointerSaveFailed"));
      } finally {
        writing.current = false;
        setSaving(false);
      }
    },
    [refresh],
  );

  return { status, saving, error, save };
}

function useInputConsent(applies: boolean) {
  const [granted, setGranted] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!applies) return;
    let cancelled = false;
    void getInputConsent().then((value) => {
      if (!cancelled) setGranted(value);
    });
    return () => {
      cancelled = true;
    };
  }, [applies]);

  // One write at a time, optimistic, then reconciled against what Rust
  // stored: a switch that says “on” while the tools refuse would be worse
  // than a moment of lag.
  const toggle = useCallback(
    async (next: boolean) => {
      if (saving) return;
      setSaving(true);
      setGranted(next);
      setError(null);
      try {
        await setInputConsent(next);
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : String(failure));
      }
      setGranted(await getInputConsent());
      setSaving(false);
    },
    [saving],
  );

  return { granted, saving, error, toggle };
}

export function ScreenAndPointerPage() {
  const t = useT(settingsPreferencesMessages);
  const macOS = pointerControlApplies();
  const windows = inputConsentApplies();
  const pointer = usePointerControl(macOS);
  const consent = useInputConsent(windows);
  const allowed = Boolean(
    pointer.status?.accessibility && pointer.status?.postEvent,
  );

  return (
    <PocketSection testId="settings-page-preferences-screen">
      <ScreenRecordingFolderRow />
      {macOS ? (
        <PocketRow
          testId="settings-row-screen-pointer"
          title={t("pointerControl")}
          secondLine={t("pointerControlDetail")}
          trailing={
            pointer.status ? (
              <SelectField
                variant="value"
                value={pointer.status.policy}
                disabled={pointer.saving}
                options={(
                  Object.keys(POLICY_LABEL_KEY) as PointerControlPolicy[]
                ).map((value) => ({
                  value,
                  label: t(POLICY_LABEL_KEY[value]),
                }))}
                onChange={(policy) =>
                  void pointer.save(policy as PointerControlPolicy)
                }
                testId="screen-pointer-policy"
                ariaLabel={t("pointerControl")}
              />
            ) : undefined
          }
          below={
            <SettingsFieldError
              error={pointer.error}
              testId="screen-pointer-error"
            />
          }
        />
      ) : null}
      {macOS ? (
        <PocketRow
          testId="settings-row-screen-permissions"
          title={t("macPermissions")}
          secondLine={t("macPermissionsDetail")}
          status={
            pointer.status && !allowed
              ? { tone: "question", text: t("notAllowed") }
              : undefined
          }
          trailing={
            !pointer.status ? undefined : allowed ? (
              t("allowed")
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void openPrivacyPaneForCurrentMacOS("accessibility").catch(
                    () => {},
                  )
                }
                data-testid="screen-open-system-settings"
              >
                {t("openSystemSettings")}
              </Button>
            )
          }
        />
      ) : null}
      {windows && consent.granted !== null ? (
        <PocketRow
          testId="settings-row-screen-consent"
          title={t("pcControl")}
          secondLine={t("pcControlDetail")}
          trailing={
            <ToggleSwitch
              checked={consent.granted}
              disabled={consent.saving}
              onCheckedChange={(next) => void consent.toggle(next)}
              aria-label={t("pcControl")}
              data-testid="screen-consent-toggle"
            />
          }
          below={
            <SettingsFieldError
              error={consent.error}
              testId="screen-consent-error"
            />
          }
        />
      ) : null}
    </PocketSection>
  );
}
